import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, Mail, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const getMfaErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "object" && error !== null) {
    const maybeMessage = "message" in error ? String((error as { message?: unknown }).message ?? "") : "";
    if (maybeMessage.includes("non-2xx")) {
      return fallback;
    }
    if (maybeMessage) {
      return maybeMessage;
    }
  }

  return fallback;
};

interface MfaDialogProps {
  open: boolean;
  userId: string;
  maskedEmail: string;
  onVerified: () => void;
  onCancel: () => void;
}

type Method = "email" | "sms";

const MfaDialog = ({ open, userId, maskedEmail, onVerified, onCancel }: MfaDialogProps) => {
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [method, setMethod] = useState<Method>("email");
  const [savedPhone, setSavedPhone] = useState<string | null>(null);
  const [destination, setDestination] = useState<string>(maskedEmail);
  const [phoneLoaded, setPhoneLoaded] = useState(false);

  // Load saved phone number when dialog opens
  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("phone_number")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      setSavedPhone(data?.phone_number ?? null);
      setPhoneLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setCode("");
      setCodeSent(false);
      setError(null);
      setMethod("email");
      setPhoneLoaded(false);
      setSavedPhone(null);
    }
  }, [open]);

  const sendCode = useCallback(async (chosenMethod: Method) => {
    setIsSending(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-mfa-code", {
        body: { action: "send", userId, method: chosenMethod },
      });
      // Email path can hit CPU time limits after sending — treat non-2xx as success only for email
      if (fnError) {
        const msg = typeof fnError === "object" && fnError !== null && "message" in fnError
          ? String((fnError as { message?: unknown }).message ?? "")
          : "";
        if (chosenMethod === "email" && msg.includes("non-2xx")) {
          // assume sent
          setDestination(maskedEmail);
          setCodeSent(true);
          return;
        }
        throw fnError;
      }
      if (data?.error) throw new Error(data.error);
      if (data?.destination) setDestination(data.destination);
      setCodeSent(true);
    } catch (err) {
      setError(getMfaErrorMessage(err, "Failed to send verification code. Please try again."));
    } finally {
      setIsSending(false);
    }
  }, [userId, maskedEmail]);

  // Auto-send via email when dialog opens (default)
  useEffect(() => {
    if (open && userId && phoneLoaded && !codeSent && !isSending) {
      sendCode("email");
    }
  }, [open, userId, phoneLoaded, codeSent, isSending, sendCode]);

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setIsVerifying(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-mfa-code", {
        body: { action: "verify", userId, code },
      });
      if (fnError) throw fnError;
      if (data?.success) {
        const trustedDevices = JSON.parse(localStorage.getItem("uc_trusted_devices") || "[]");
        trustedDevices.push({ userId, fingerprint: getDeviceFingerprint(), trustedAt: Date.now() });
        localStorage.setItem("uc_trusted_devices", JSON.stringify(trustedDevices));
        onVerified();
      } else {
        setError(data?.error || "Invalid code");
      }
    } catch (err) {
      setError(getMfaErrorMessage(err, "Verification failed. Please try the latest code."));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSwitchMethod = (next: Method) => {
    if (next === method || isSending) return;
    if (next === "sms" && !savedPhone) {
      setError("No mobile number saved. Add one in Profile → Preferences to use SMS.");
      return;
    }
    setMethod(next);
    setCode("");
    setCodeSent(false);
    setError(null);
    sendCode(next);
  };

  const handleResend = () => {
    setCode("");
    setCodeSent(false);
    setError(null);
    sendCode(method);
  };

  const channelLabel = method === "sms" ? "text message" : "email";

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Verify Your Identity
          </DialogTitle>
          <DialogDescription>
            {codeSent
              ? `We sent a 6-digit code via ${channelLabel} to ${destination}. Enter it below.`
              : `Sending verification code via ${channelLabel}...`}
          </DialogDescription>
        </DialogHeader>

        {/* Method selector */}
        <div className="grid grid-cols-2 gap-2" role="tablist" aria-label="Verification method">
          <button
            type="button"
            role="tab"
            aria-selected={method === "email"}
            onClick={() => handleSwitchMethod("email")}
            disabled={isSending || isVerifying}
            className={`flex items-center justify-center gap-2 px-3 py-2 text-sm border rounded-md transition-colors ${
              method === "email"
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-background text-foreground hover:bg-muted"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Mail className="h-4 w-4" />
            Email
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={method === "sms"}
            onClick={() => handleSwitchMethod("sms")}
            disabled={isSending || isVerifying || !savedPhone}
            title={!savedPhone ? "Add a mobile number in Profile → Preferences" : undefined}
            className={`flex items-center justify-center gap-2 px-3 py-2 text-sm border rounded-md transition-colors ${
              method === "sms"
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-background text-foreground hover:bg-muted"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <MessageSquare className="h-4 w-4" />
            Text {!savedPhone && phoneLoaded && <span className="text-[10px] opacity-70">(no #)</span>}
          </button>
        </div>

        <div className="flex flex-col items-center gap-6 py-4">
          {isSending ? (
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          ) : (
            <>
              <InputOTP maxLength={6} value={code} onChange={setCode}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>

              {error && (
                <p className="text-sm text-destructive text-center">{error}</p>
              )}

              <div className="flex flex-col gap-2 w-full">
                <Button
                  onClick={handleVerify}
                  disabled={code.length !== 6 || isVerifying}
                  className="w-full"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    "Verify"
                  )}
                </Button>

                <Button variant="ghost" size="sm" onClick={handleResend} disabled={isSending}>
                  Resend Code
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Simple device fingerprint based on browser characteristics
function getDeviceFingerprint(): string {
  const nav = navigator;
  const raw = [
    nav.userAgent,
    nav.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
  ].join("|");
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const chr = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0;
  }
  return hash.toString(36);
}

export { getDeviceFingerprint };
export default MfaDialog;
